import { Component, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import { IObject, IValueObject, IWebshop } from '../../../../type_definitions/datatypes';
import { FormattingService } from '../../../shared/services/formatting.service';
import { WebshopDataService } from '../../webshop/webshop-data.service';
import { UPLOAD_STEP1, UPLOAD_WIZARD, UploadService } from '../upload.service';
import * as _ from 'underscore';
import { GaValidateService } from '../../../shared/formly/ga-validate.service';
import { Transition } from '@uirouter/angular';
import { ReportFiltersUtil } from '../../../shared/report/report-filters-util.service';
import { GaWizard } from '../../../shared/wizard/ga-wizard';
import { GaWizardService } from '../../../shared/wizard/ga-wizard.service';

interface IModel {
  webshopSelection:IValueObject;
  webshop?:IWebshop;
}

@Component({
  selector: 'upload-local-webshop-settings',
  template: `<formly [form]="form" [fields]="fields" [model]="model"></formly>`
})
export class LocalWebshopSettingsComponent implements OnInit {
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public model:IModel = {
    webshopSelection: { id: false }
  };

  private wizard:GaWizard;
  private webshopField:IExtraFieldConfigurationObject;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private webshopDataService:WebshopDataService,
    private wizardService:GaWizardService,
    private uploadService:UploadService,
    private validateService:GaValidateService,
    private reportFiltersUtil:ReportFiltersUtil,
    private transition:Transition
  ) {
    /**/
  }

  public ngOnInit():void {
    this.wizard = this.wizardService.getInstance(UPLOAD_WIZARD);
    this.wizard.title = 'GA.UPLOADS.SELECT_WEBSHOP';

    const contextParams:IObject = this.reportFiltersUtil.paramToFilters(this.transition.params()?.context);

    // transform to correct type of datatype
    _.each(contextParams, (value:string, param:string) => {
      contextParams[ param ] = Number(value) || value;
    });

    // init model
    this.model = _.extend(this.model, <IModel>this.uploadService.model.context, contextParams);

    if (this.model.webshop) {
      this.model.webshopSelection = { id: true };
    }

    // create formly fields
    this.fields = this.createFields();

    // fetch list items
    this.fillFields();

    // set validation before step change
    const onBeforeStepChange = (invalid:boolean) => this.wizard.step(UPLOAD_STEP1).onBeforeStepChange = ():boolean => invalid;
    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => onBeforeStepChange(false))
      .catch((errors:string[]) => onBeforeStepChange(!!errors.length));

    // register listener
    this.listen();
  }

  private listen():void {
    this.form.valueChanges.subscribe((model:IModel) => {
      this.uploadService.model = _.extend(this.uploadService.model, { context: model });
    });
  }

  private onWebshopSelectionChange = (value:IValueObject) => {
    if (value) {
      this.webshopField.templateOptions.disabled = !value.id;
      this.webshopField.templateOptions.required = <boolean>value.id;
    }
  };

  private fillFields():void {
    this.formlyService.fillField(this.webshopField, this.webshopDataService.webshops())
      .finally(() => {
        // init webshop selection change
        this.onWebshopSelectionChange(this.model.webshopSelection);
      });
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const options:IValueObject[] = [
      {
        id: false,
        value: 'GA.UPLOADS.WEBSHOP_SELECTION_1'
      },
      {
        id: true,
        value: 'GA.UPLOADS.WEBSHOP_SELECTION_2'
      }
    ];

    const valueClass:string = 'col-sm-12';

    return [
      this.formlyService.aRadio('webshopSelection')
        .withoutWrapper()
        .withFormat(this.formattingService.formatLov)
        .withValueClass(valueClass)
        .withOptions(options)
        .onChange(this.onWebshopSelectionChange)
        .build(),
      this.webshopField = this.formlyService.aSelect('webshop', 'GA.GENERAL.WEBSHOP')
        .withLabelProp('name')
        .withValueProp('id')
        .withoutWrapper()
        .withValueClass(valueClass)
        .hidePlaceholder()
        .build()
    ];
  }
}